import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../services/backendUrl';
const API = `${BACKEND_URL}/api`;
// Auth requests must not be cancelled/deduplicated with page/report navigation.
const authClient = axios.create();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  const [authAttempt, setAuthAttempt] = useState(0);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const accessRefresh = useRef(null);
  const refreshAccess = useCallback(async () => {
    if (!token) return;
    if (accessRefresh.current) return accessRefresh.current;
    const activeToken = token;
    accessRefresh.current = authClient.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (localStorage.getItem('token') === activeToken) { setUser(r.data.user); setCompany(r.data.company); } })
      .finally(() => { accessRefresh.current = null; });
    return accessRefresh.current;
  }, [token]);
  useEffect(() => {
    const refresh = () => refreshAccess().catch(() => {});
    window.addEventListener('focus', refresh);
    window.addEventListener('access-permissions-changed', refresh);
    return () => { window.removeEventListener('focus', refresh); window.removeEventListener('access-permissions-changed', refresh); };
  }, [refreshAccess]);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check auth on mount
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const checkAuth = async () => {
      setSessionError(null);
      if (token) {
        setLoading(true);
        try {
          const response = await authClient.get(`${API}/auth/me`, {
            signal: controller.signal, headers: { Authorization: `Bearer ${token}` },
          });
          if (active && localStorage.getItem('token') === token) {
            setUser(response.data.user);
            setCompany(response.data.company);
          }
        } catch (error) {
          // An aborted request/network outage is not proof of invalid credentials.
          if (active && localStorage.getItem('token') === token) {
            if ([401, 403].includes(error.response?.status)) logout();
            else if (!axios.isCancel(error)) setSessionError('Your sign-in is saved, but the server could not verify it. Retry the connection to continue.');
          }
        }
      }
      if (active) setLoading(false);
    };
    checkAuth();
    return () => { active = false; controller.abort(); };
  }, [token, authAttempt]);

  const retrySession = () => { setLoading(true); setSessionError(null); setAuthAttempt(value => value + 1); };

  const activateSession = (data) => {
    if (!data?.token || !data?.user?.id) throw new Error('Invalid authentication response');
    localStorage.setItem('token', data.token);
    setSessionError(null);
    setLoading(true);
    setToken(data.token);
    setUser(data.user);
    setCompany(data.company || { code: data.company_code });
  };

  const signupCompany = async (companyName, phone, ownerName, ownerDigits4) => {
    const response = await authClient.post(`${API}/auth/signup/company`, {
      company_name: companyName.trim(),
      phone: phone.trim(),
      owner_name: ownerName.trim(),
      owner_digits_4: ownerDigits4,
    });
    // Keep the confirmation/code visible. Activate only on the explicit Continue.
    return response.data;
  };

  const signupEmployee = async (companyCode, phone, name) => {
    const response = await authClient.post(`${API}/auth/signup/employee`, {
      company_code: companyCode.replace(/\s/g, '').toUpperCase(),
      phone: phone.trim(),
      name: name.trim()
    });
    return response.data;
  };

  const login = async (companyCode, phone) => {
    const response = await authClient.post(`${API}/auth/login`, {
      company_code: companyCode.replace(/\s/g, '').toUpperCase(),
      phone: phone.trim()
    });
    activateSession(response.data);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setCompany(null);
    setSessionError(null);
    setLoading(false);
  };

  // Auto-refresh token every 20 hours (tokens expire in 24h)
  useEffect(() => {
    if (!token) return;
    const REFRESH_INTERVAL = 20 * 60 * 60 * 1000; // 20 hours
    const timer = setInterval(async () => {
      try {
        const res = await authClient.post(`${API}/auth/refresh`, {}, { headers: { Authorization: `Bearer ${token}` } });
        const newToken = res.data.token;
        if (localStorage.getItem('token') !== token) return;
        localStorage.setItem('token', newToken);
        setToken(newToken);
      } catch (error) {
        if (localStorage.getItem('token') === token && [401, 403].includes(error.response?.status)) logout();
      }
    }, REFRESH_INTERVAL);
    return () => clearInterval(timer);
  }, [token]);

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.is_owner || user.is_super_admin || (user.legacy_access && user.is_admin)) return true;
    if (permission.startsWith('master_')) {
      const feature = permission.slice(7);
      if (user.permission_keys?.some(key => key.startsWith(`masters.${feature}.`))) return true;
      if (feature === 'item' && user.permission_keys?.some(key => /^masters\.(unit|vehicle|equipment|item_category|expense_category|labour_category|hsn_code)\./.test(key))) return true;
    }
    if (permission === 'report_view' && user.permission_keys?.some(key => key.startsWith('reports.') && key.endsWith('.view'))) return true;
    return user.permission_keys?.includes(permission) || user.permissions?.includes(permission);
  };

  const canAccessSite = (siteId) => {
    if (!user) return false;
    if (user.is_owner || user.is_admin) return true;
    if (!user.assigned_site_ids || user.assigned_site_ids.length === 0) return true;
    return user.assigned_site_ids.includes(siteId);
  };

  const value = {
    user,
    company,
    loading,
    sessionError,
    retrySession,
    token,
    signupCompany,
    activateSession,
    signupEmployee,
    login,
    logout,
    hasPermission,
    refreshAccess,
    canAccessSite,
    isAuthenticated: !!user && !!token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
